import { FormHelperText } from '@mui/material';
import Box from '@mui/material/Box';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React, { Children } from 'react';

import { createChangeEvent } from '@/utility/createChangeEvent';

interface CardSelectItemProps {
  readonly title: string;
  readonly description?: string;
  readonly value: string;
  readonly checked?: boolean;

  onClick(value: string): void;
}

interface Props {
  readonly error?: string | null;
  readonly value: string;
  readonly children: React.ReactNode;

  onChange?(event: React.ChangeEvent<HTMLInputElement>): void;
}

export const CardSelect: React.FC<Props> = (props: Props): React.ReactElement => {
  const { value, error, children, onChange } = props;

  const ref = React.createRef<HTMLInputElement | null>();
  const array = Children.toArray(children).filter(React.isValidElement<CardSelectItemProps>);
  if (array.length !== Children.count(children)) {
    throw new Error('only <CardSelectItem /> are allowd as children of <CardSelect />');
  }

  const handleItemClick = React.useCallback(
    (value: string): void => {
      const { current: input } = ref;
      const event = createChangeEvent<HTMLInputElement>(input, value);
      if (event) {
        onChange?.(event);
      }
    },
    [onChange, ref],
  );

  return (
    <Box ref={ref} sx={styles.container}>
      {array.map((child: React.ReactElement<CardSelectItemProps>): React.ReactElement => {
        const { value: childValue } = child.props;

        return React.cloneElement(child, {
          ...child.props,
          checked: value === childValue,
          onClick: handleItemClick,
        });
      })}
      {error && <FormHelperText error={true}>{error}</FormHelperText>}
    </Box>
  );
};

const styles: Record<string, SystemStyleObject> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
  },
};
